import React, { useMemo, useEffect, useRef, useCallback } from 'react';
import { FiFolder, FiLayers, FiCrosshair, FiDownload } from 'react-icons/fi';
import { downloadKmlLayer } from '../services/api';
import {
  layerSelectionStorageKey,
  getBoundsForLayerIds,
} from '../utils/layerUtils';
import './LayerTreeSelector.css';

function IndeterminateCheckbox({ checked, indeterminate, onChange, title, 'aria-label': ariaLabel }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = !!indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      className="layer-tree-cb"
      checked={checked}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
      title={title}
      aria-label={ariaLabel}
    />
  );
}

export default function LayerTreeSelector({
  projectId,
  project,
  selectedLayerIds,
  onSelectionChange,
  onZoomToBounds,
}) {
  const layers = project?.kml_layers || [];
  const folders = project?.folders || [];
  const allLayerIds = useMemo(() => layers.map((l) => l.id), [layers]);

  useEffect(() => {
    if (!projectId || !allLayerIds.length) return;
    const key = layerSelectionStorageKey(projectId);
    const raw = localStorage.getItem(key);
    let initial = [...allLayerIds];
    if (raw != null) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) {
          const valid = new Set(allLayerIds);
          const filt = parsed.map(Number).filter((id) => valid.has(id));
          if (filt.length) initial = filt;
        }
      } catch {
        /* use all layers */
      }
    }
    onSelectionChange(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, allLayerIds.join(',')]);

  useEffect(() => {
    if (!projectId || selectedLayerIds == null) return;
    localStorage.setItem(layerSelectionStorageKey(projectId), JSON.stringify(selectedLayerIds));
  }, [projectId, selectedLayerIds]);

  const byFolderId = useMemo(() => {
    const m = new Map();
    layers.forEach((ly) => {
      const fk =
        ly.folder_id == null || ly.folder_id === '' ? '__root' : Number(ly.folder_id);
      if (!m.has(fk)) m.set(fk, []);
      m.get(fk).push(ly);
    });
    m.forEach((arr) => arr.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
    return m;
  }, [layers]);

  const folderNameById = useMemo(() => {
    const o = {};
    folders.forEach((f) => {
      o[f.id] = f.name;
    });
    return o;
  }, [folders]);

  const checked = useMemo(() => new Set(selectedLayerIds || []), [selectedLayerIds]);

  const zoomToLayerIds = useCallback(
    (ids) => {
      if (!onZoomToBounds || !project) return;
      const b = getBoundsForLayerIds(project, ids);
      onZoomToBounds(b);
    },
    [onZoomToBounds, project]
  );

  const toggle = useCallback(
    (id) => {
      const next = new Set(checked);
      if (next.has(Number(id))) next.delete(Number(id));
      else next.add(Number(id));
      onSelectionChange([...next]);
    },
    [checked, onSelectionChange]
  );

  const toggleLayers = useCallback(
    (layerList) => {
      const ids = layerList.map((l) => l.id);
      const allOn = ids.length > 0 && ids.every((id) => checked.has(Number(id)));
      const next = new Set(checked);
      if (allOn) {
        ids.forEach((id) => next.delete(Number(id)));
      } else {
        ids.forEach((id) => next.add(Number(id)));
      }
      onSelectionChange([...next]);
    },
    [checked, onSelectionChange]
  );

  const selectAll = () => onSelectionChange([...allLayerIds]);
  const selectNone = () => onSelectionChange([]);

  const renderZoomBtn = (ids, label) => {
    if (!onZoomToBounds) return null;
    return (
      <button
        type="button"
        className="layer-tree-zoom-btn"
        title={`Zoom map to ${label}`}
        aria-label={`Zoom map to ${label}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          zoomToLayerIds(ids);
        }}
      >
        <FiCrosshair aria-hidden />
      </button>
    );
  };

  const kmlDownloadFilename = (ly) => {
    const src = (ly.source_filename || '').trim();
    if (src) return src.replace(/^.*[/\\]/, '');
    const name = (ly.name || 'layer').trim();
    return `${name.replace(/[^\w.\- ]/g, '_') || 'layer'}.kml`;
  };

  const renderDownloadBtn = (ly) => {
    if (!projectId) return null;
    const fname = kmlDownloadFilename(ly);
    return (
      <button
        type="button"
        className="layer-tree-download-btn"
        title={`Download KML: ${fname}`}
        aria-label={`Download KML for ${ly.name || 'layer'}`}
        onClick={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          try {
            await downloadKmlLayer(projectId, ly.id, fname);
          } catch (err) {
            const msg = err.response?.data?.error || err.message || 'Download failed';
            window.alert(msg);
          }
        }}
      >
        <FiDownload aria-hidden />
      </button>
    );
  };

  const renderLayerRow = (ly) => (
    <div key={`ly-${ly.id}`} className="layer-tree-layer-row">
      <label className="layer-tree-layer-label">
        <input
          type="checkbox"
          className="layer-tree-cb"
          checked={checked.has(ly.id)}
          onChange={() => toggle(ly.id)}
        />
        <span className="layer-tree-layer-name">{ly.name}</span>
      </label>
      <span className="layer-tree-row-actions">
        {renderDownloadBtn(ly)}
        {renderZoomBtn([ly.id], ly.name || 'layer')}
      </span>
    </div>
  );

  const folderCheckboxState = (layerList) => {
    const ids = layerList.map((l) => l.id);
    const n = ids.length;
    const numChecked = ids.filter((id) => checked.has(id)).length;
    const all = n > 0 && numChecked === n;
    const none = numChecked === 0;
    return { all, none, indeterminate: !none && !all };
  };

  if (!projectId || !layers.length) return null;

  const rootLayers = byFolderId.get('__root') || [];
  const folderIdsSorted = [...folders]
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    .map((f) => f.id);

  return (
    <div className="layer-tree-panel" role="region" aria-label="Map layers">
      <div className="layer-tree-panel-header">
        <FiLayers className="layer-tree-panel-icon" aria-hidden />
        <h3 className="layer-tree-panel-title">Layers</h3>
      </div>
      <div className="layer-tree-toolbar">
        <button type="button" className="layer-tree-btn" onClick={selectAll}>
          Select all
        </button>
        <button type="button" className="layer-tree-btn" onClick={selectNone}>
          Clear
        </button>
        <span className="layer-tree-count-pill" title="Visible layers / total">
          {checked.size}/{allLayerIds.length} visible
        </span>
      </div>

      <div className="layer-tree-scroll">
        {rootLayers.length > 0 && (
          <details className="layer-tree-folder">
            <summary className="layer-tree-folder-summary">
              <IndeterminateCheckbox
                checked={folderCheckboxState(rootLayers).all}
                indeterminate={folderCheckboxState(rootLayers).indeterminate}
                onChange={() => toggleLayers(rootLayers)}
                title="Toggle all ungrouped layers"
                aria-label="Toggle all ungrouped layers"
              />
              <span className="layer-tree-folder-label">Ungrouped</span>
              <span className="layer-tree-summary-trail">
                <span className="layer-tree-badge">{rootLayers.length}</span>
                {renderZoomBtn(
                  rootLayers.map((l) => l.id),
                  'ungrouped layers'
                )}
              </span>
            </summary>
            <div className="layer-tree-folder-body">{rootLayers.map((ly) => renderLayerRow(ly))}</div>
          </details>
        )}

        {folderIdsSorted.map((fid) => {
          const inFolder = byFolderId.get(Number(fid)) || [];
          if (!inFolder.length) return null;
          const { all, indeterminate } = folderCheckboxState(inFolder);
          const name = folderNameById[fid] || `Folder ${fid}`;
          return (
            <details key={`folder-${fid}`} className="layer-tree-folder">
              <summary className="layer-tree-folder-summary">
                <IndeterminateCheckbox
                  checked={all}
                  indeterminate={indeterminate}
                  onChange={() => toggleLayers(inFolder)}
                  title={`Toggle all layers in ${name}`}
                  aria-label={`Toggle all layers in ${name}`}
                />
                <FiFolder className="layer-tree-folder-icon" aria-hidden />
                <span className="layer-tree-folder-label">{name}</span>
                <span className="layer-tree-summary-trail">
                  <span className="layer-tree-badge">{inFolder.length}</span>
                  {renderZoomBtn(inFolder.map((l) => l.id), name)}
                </span>
              </summary>
              <div className="layer-tree-folder-body">{inFolder.map((ly) => renderLayerRow(ly))}</div>
            </details>
          );
        })}
      </div>
    </div>
  );
}
